import React, { useState } from 'react';
import { getSoundManager } from '../game/systems/SoundManager';

interface OptionsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  volume: number;
  sfxVolume: number;
  musicVolume: number;
  cameraZoom: number;
  showPlayerIdentityUI: boolean;
  showMyNameLabel: boolean;
  showMyFlag: boolean;
  showOtherPlayerNames: boolean;
  showOtherPlayerFlags: boolean;
  backgroundMusicEnabled: boolean;
  worldEventNotificationsEnabled: boolean;
  serverFeedbackEnabled: boolean;
  onVolumeChange: (volume: number) => void;
  onSfxVolumeChange: (volume: number) => void;
  onMusicVolumeChange: (volume: number) => void;
  onZoomChange: (zoom: number) => void;
  onShowPlayerIdentityUIChange: (show: boolean) => void;
  onShowMyNameLabelChange: (show: boolean) => void;
  onShowMyFlagChange: (show: boolean) => void;
  onShowOtherPlayerNamesChange: (show: boolean) => void;
  onShowOtherPlayerFlagsChange: (show: boolean) => void;
  onBackgroundMusicEnabledChange: (enabled: boolean) => void;
  onWorldEventNotificationsEnabledChange: (enabled: boolean) => void;
  onServerFeedbackEnabledChange: (enabled: boolean) => void;
  onShowDailyRewards?: () => void;
  onShowReferral?: () => void;
}

type TabType = 'audio' | 'display' | 'actions';

export const OptionsMenu: React.FC<OptionsMenuProps> = ({
  isOpen,
  onClose,
  volume,
  sfxVolume,
  musicVolume,
  cameraZoom,
  showPlayerIdentityUI,
  showMyNameLabel,
  showMyFlag,
  showOtherPlayerNames,
  showOtherPlayerFlags,
  backgroundMusicEnabled,
  worldEventNotificationsEnabled,
  serverFeedbackEnabled,
  onVolumeChange,
  onSfxVolumeChange,
  onMusicVolumeChange,
  onZoomChange,
  onShowPlayerIdentityUIChange,
  onShowMyNameLabelChange,
  onShowMyFlagChange,
  onShowOtherPlayerNamesChange,
  onShowOtherPlayerFlagsChange,
  onBackgroundMusicEnabledChange,
  onWorldEventNotificationsEnabledChange,
  onServerFeedbackEnabledChange,
  onShowDailyRewards,
  onShowReferral,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('audio');

  if (!isOpen) return null;

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div 
        className="options-menu-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          backgroundColor: '#0d1117',
          border: '1px solid #30363d',
          borderRadius: '6px',
          padding: '20px',
          zIndex: 10000,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#f0f6fc', fontSize: '20px', fontWeight: 'bold' }}>OPTIONS</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px',
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #30363d', paddingBottom: '0' }}>
          {(['audio', 'display', 'actions'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: activeTab === tab ? 'bold' : 'normal',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #58a6ff' : '2px solid transparent',
                color: activeTab === tab ? '#58a6ff' : '#8b949e',
                cursor: 'pointer',
                textTransform: 'uppercase',
                transition: 'color 0.2s',
              }}
            >
              {tab === 'audio' && 'Audio'}
              {tab === 'display' && 'Display'}
              {tab === 'actions' && 'Actions'}
            </button>
          ))}
        </div>

        {/* Audio Tab */}
        {activeTab === 'audio' && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase' }}>
            Audio
          </h3>
          
          {/* Master Volume */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Master Volume</label>
              <span style={{ color: '#8b949e', fontSize: '13px' }}>{volume}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => {
                  const newVolume = parseInt(e.target.value, 10);
                  onVolumeChange(newVolume);
                  try {
                    localStorage.setItem('masterVolume', String(newVolume / 100));
                  } catch {
                    // ignore
                  }
                  const soundManager = getSoundManager();
                  if (soundManager) {
                    soundManager.setMasterVolume(newVolume / 100);
                  }
                }}
                style={{ flex: 1 }}
              />
              <button
                onClick={() => onVolumeChange(volume === 0 ? 100 : 0)}
                style={{
                  padding: '4px 8px',
                  fontSize: '14px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#f0f6fc',
                  cursor: 'pointer',
                }}
                title={volume === 0 ? 'Unmute All' : 'Mute All'}
              >
                {volume === 0 ? '🔇' : '🔊'}
              </button>
            </div>
          </div>

          {/* SFX Volume */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Sound Effects</label>
              <span style={{ color: '#8b949e', fontSize: '13px' }}>{sfxVolume}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={sfxVolume}
                onChange={(e) => onSfxVolumeChange(parseInt(e.target.value, 10))}
                style={{ flex: 1 }}
              />
              <button
                onClick={() => onSfxVolumeChange(sfxVolume === 0 ? 100 : 0)}
                style={{
                  padding: '4px 8px',
                  fontSize: '14px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#f0f6fc',
                  cursor: 'pointer',
                }}
                title={sfxVolume === 0 ? 'Unmute SFX' : 'Mute SFX'}
              >
                {sfxVolume === 0 ? '🔇' : '🔊'}
              </button>
            </div>
          </div>

          {/* Music Volume */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Background Music</label>
              <span style={{ color: '#8b949e', fontSize: '13px' }}>{musicVolume}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={musicVolume}
                onChange={(e) => onMusicVolumeChange(parseInt(e.target.value, 10))}
                style={{ flex: 1 }}
              />
              <button
                onClick={() => onMusicVolumeChange(musicVolume === 0 ? 100 : 0)}
                style={{
                  padding: '4px 8px',
                  fontSize: '14px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#f0f6fc',
                  cursor: 'pointer',
                }}
                title={musicVolume === 0 ? 'Unmute Music' : 'Mute Music'}
              >
                {musicVolume === 0 ? '🔇' : '🎵'}
              </button>
            </div>
          </div>

          {/* Background Music Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Enable Background Music</label>
            <input
              type="checkbox"
              checked={backgroundMusicEnabled}
              onChange={(e) => onBackgroundMusicEnabledChange(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>
        )}

        {/* Display Tab */}
        {activeTab === 'display' && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase' }}>
            Camera
          </h3>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Zoom Level</label>
              <span style={{ color: '#8b949e', fontSize: '13px' }}>{(cameraZoom / 100).toFixed(1)}x</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => onZoomChange(Math.max(100, cameraZoom - 10))}
                style={{
                  padding: '4px 12px',
                  fontSize: '16px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#f0f6fc',
                  cursor: 'pointer',
                }}
                title="Zoom Out"
              >
                −
              </button>
              <input
                type="range"
                min="100"
                max="250"
                value={cameraZoom}
                onChange={(e) => {
                  const newZoom = Math.max(100, Math.min(250, parseInt(e.target.value, 10)));
                  onZoomChange(newZoom);
                }}
                style={{ flex: 1 }}
              />
              <button
                onClick={() => onZoomChange(Math.min(250, cameraZoom + 10))}
                style={{
                  padding: '4px 12px',
                  fontSize: '16px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#f0f6fc',
                  cursor: 'pointer',
                }}
                title="Zoom In"
              >
                +
              </button>
            </div>
          </div>

          <h3 style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', marginTop: '20px', textTransform: 'uppercase' }}>
            Display
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Show Player Identity UI</label>
              <input
                type="checkbox"
                checked={showPlayerIdentityUI}
                onChange={(e) => onShowPlayerIdentityUIChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Show My Name Label</label>
              <input
                type="checkbox"
                checked={showMyNameLabel}
                onChange={(e) => onShowMyNameLabelChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Show My Flag</label>
              <input
                type="checkbox"
                checked={showMyFlag}
                onChange={(e) => onShowMyFlagChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Show Other Player Names</label>
              <input
                type="checkbox"
                checked={showOtherPlayerNames}
                onChange={(e) => onShowOtherPlayerNamesChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Show Other Player Flags</label>
              <input
                type="checkbox"
                checked={showOtherPlayerFlags}
                onChange={(e) => onShowOtherPlayerFlagsChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>

          <h3 style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', marginTop: '20px', textTransform: 'uppercase' }}>
            Notifications
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>World Event Notifications</label>
              <input
                type="checkbox"
                checked={worldEventNotificationsEnabled}
                onChange={(e) => onWorldEventNotificationsEnabledChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#c9d1d9', fontSize: '13px' }}>Server Feedback Messages</label>
              <input
                type="checkbox"
                checked={serverFeedbackEnabled}
                onChange={(e) => onServerFeedbackEnabledChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
        </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', textTransform: 'uppercase' }}>
            Actions
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {onShowDailyRewards && (
              <button
                onClick={() => {
                  onShowDailyRewards();
                  onClose();
                }}
                style={{
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  background: '#238636',
                  border: '1px solid #2ea043',
                  borderRadius: '4px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2ea043')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#238636')}
              >
                Daily Rewards
              </button>
            )}
            {onShowReferral && (
              <button
                onClick={() => {
                  onShowReferral();
                  onClose();
                }}
                style={{
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  background: '#238636',
                  border: '1px solid #2ea043',
                  borderRadius: '4px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2ea043')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#238636')}
              >
                Referral Program
              </button>
            )}
          </div>
        </div>
        )}

        {/* Close Button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 40px',
              fontSize: '14px',
              fontWeight: 'bold',
              background: '#238636',
              border: '1px solid #2ea043',
              borderRadius: '6px',
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2ea043')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#238636')}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};
